import type { HelpGuide } from '../types';

export const lvaGuide: HelpGuide = {
  key: 'lva',
  title: 'ทะเบียนทรัพย์สินมูลค่าน้อย (LVA Register)',
  subtitle: 'เมนู: บัญชีแยกประเภท ▸ ทะเบียนทรัพย์สินมูลค่าน้อย (LVA)',
  updated: 'ปรับปรุง 20 มิ.ย. 2569',
  docxUrl: '/help/lva/คู่มือการใช้งาน_ทะเบียนทรัพย์สินมูลค่าน้อย_DCC-ERP.docx',
  sections: [
    {
      id: 'overview',
      heading: '1. หน้านี้คืออะไร และใช้ทำอะไร',
      blocks: [
        {
          kind: 'para',
          text: 'หน้า “ทะเบียนทรัพย์สินมูลค่าน้อย” (Low-Value Asset / LVA) คือทะเบียนคุมของใช้/อุปกรณ์ที่มูลค่าต่ำกว่าเกณฑ์ตั้งเป็นสินทรัพย์ถาวร (บริษัทกำหนดไว้ที่ ฿50,000 ตามนโยบายความมีนัยสำคัญ TFRS for NPAEs) เช่น เก้าอี้ เครื่องพิมพ์ สว่าน กล้องวงจรปิด โน้ตบุ๊ก',
        },
        {
          kind: 'callout',
          text: 'สำคัญ: ของมูลค่าต่ำจะถูกลงเป็นค่าใช้จ่ายทันทีตอนซื้อ ทะเบียนนี้จึงเป็นเพียงบันทึกความจำ (Memorandum) ไว้ติดตามของจริง — ไม่กระทบยอดในงบดุล (Off-balance)',
        },
        {
          kind: 'para',
          text: 'การทำงานแบ่งเป็น 4 ขั้น และหน้าจอมี 5 แท็บรองรับ:',
        },
        {
          kind: 'bullets',
          items: [
            {
              lead: '① ขึ้นทะเบียน LVA — ',
              text: 'บันทึกของเข้าทะเบียน (ระบบลงค่าใช้จ่ายให้อัตโนมัติ)',
            },
            { lead: '② Tag & QR Print — ', text: 'พิมพ์ QR/บาร์โค้ดติดของจริง เพื่อสแกนภายหลัง' },
            { lead: '③ Physical Count — ', text: 'ตรวจนับของจริงเทียบทะเบียนด้วยการสแกน QR' },
            {
              lead: '④ Lifecycle — ',
              text: 'โอนย้าย/เปลี่ยนผู้ถือครอง/แจ้งชำรุด-สูญหาย/จำหน่าย พร้อมบันทึกประวัติ',
            },
          ],
        },
        {
          kind: 'image',
          src: '/help/lva/01-register.png',
          caption:
            'ภาพรวม — การ์ด KPI 4 ใบ แถบแนวคิด 4 ขั้น ตัวกรอง และตารางทะเบียนพร้อมป้ายหมวด/สถานะ/QR',
        },
      ],
    },
    {
      id: 'anatomy',
      heading: '2. ส่วนประกอบของหน้าจอ (แท็บทะเบียน)',
      blocks: [
        {
          kind: 'bullets',
          items: [
            { lead: 'พิมพ์ QR Tag — ', text: 'พิมพ์ QR/ป้ายติดทรัพย์สินครั้งละหลายรายการ (Batch)' },
            { lead: 'เริ่มตรวจนับ — ', text: 'เปิดรอบตรวจนับของจริง' },
            { lead: 'เพิ่ม LVA ใหม่ — ', text: 'เปิดฟอร์มบันทึกของใหม่เข้าทะเบียน (ปุ่มสีแดง)' },
          ],
        },
        {
          kind: 'para',
          text: 'การ์ด KPI 4 ใบ: รายการในทะเบียน · มูลค่ารวม (จดไว้ · Off-balance) · ใช้งาน (in-use) · สูญหาย ตัวกรอง: ค้นหา/หมวด/สถานะ/สาขา/หน่วยงาน พร้อมปุ่ม นำเข้า Excel · ส่งออก Excel',
        },
      ],
    },
    {
      id: 'add',
      heading: '3. การเพิ่ม LVA ใหม่',
      blocks: [
        {
          kind: 'para',
          text: 'กดปุ่ม เพิ่ม LVA ใหม่ กรอก: ชื่อสินค้า · หมวด · มูลค่า (ต้อง < ฿50,000) · บัญชีเครดิต (เงินสด-ธนาคาร) · วันที่ซื้อ · Serial · หมดประกัน · สภาพ · เลขที่เอกสารอ้างอิง · จำนวน/หน่วย · สาขา · ผู้รับผิดชอบ · สถานที่ใช้งาน เลขที่ LVA-YYYY-NNNNN ออกให้อัตโนมัติ',
        },
        {
          kind: 'callout',
          text: 'การลงบัญชีอัตโนมัติ: เมื่อบันทึก ระบบ Dr ค่าใช้จ่าย (LVA) / Cr บัญชีเงินสด-ธนาคารที่เลือก เต็มจำนวนทันที — ทะเบียนใช้ติดตามของเท่านั้น ไม่ขึ้นงบดุล หากมูลค่า ≥ เกณฑ์ ระบบเตือนให้ไปลงทะเบียนเป็นสินทรัพย์ถาวรแทน',
        },
        {
          kind: 'image',
          src: '/help/lva/02-add.png',
          caption:
            'ฟอร์มเพิ่ม LVA ใหม่ — เลือกหมวด/ผู้รับผิดชอบแบบค้นหา และหมายเหตุการลงบัญชีค่าใช้จ่ายอัตโนมัติ',
        },
      ],
    },
    {
      id: 'lifecycle',
      heading: '4. รายละเอียดทรัพย์สิน และวงจรชีวิต (Lifecycle)',
      blocks: [
        {
          kind: 'para',
          text: 'คลิกที่แถวในทะเบียนเพื่อเปิดการ์ดรายละเอียด แสดงข้อมูลหลัก (หมวด/มูลค่า/สาขา/สถานที่/ผู้รับผิดชอบ/วันที่ซื้อ) พร้อม “ประวัติการดำเนินการ” และ “รูปภาพ/เอกสาร” ที่แนบไว้',
        },
        {
          kind: 'image',
          src: '/help/lva/03-detail.png',
          caption:
            'การ์ดรายละเอียด — ข้อมูลหลัก ประวัติการดำเนินการ ไฟล์แนบ และปุ่มดำเนินการวงจรชีวิต',
        },
        { kind: 'para', text: 'ปุ่มดำเนินการ Lifecycle (บันทึกเป็นประวัติที่ลบไม่ได้):' },
        {
          kind: 'bullets',
          items: [
            { lead: 'โอนย้าย — ', text: 'ย้ายของไปสาขา/หน่วยงานอื่น' },
            { lead: 'เปลี่ยนผู้ถือครอง — ', text: 'เปลี่ยนผู้รับผิดชอบ' },
            { lead: 'แจ้งชำรุด — ', text: 'บันทึกว่าของชำรุด พร้อมระบุสภาพ' },
            {
              lead: 'แจ้งสูญหาย — ',
              text: 'บันทึกของสูญหาย (ต้องมีผู้อนุมัติ และผู้อนุมัติต้องไม่ใช่ผู้บันทึก)',
            },
            { lead: 'จำหน่าย / ขายซาก — ', text: 'จำหน่าย (ต้องมีผู้อนุมัติ)' },
          ],
        },
      ],
    },
    {
      id: 'qr',
      heading: '5. การพิมพ์ QR / ป้ายติดทรัพย์สิน',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ QR Code Generator แสดง QR ของทุกรายการให้พิมพ์ติดของจริง เลือกขนาดสติกเกอร์ได้ (A4 · 8/24/65 ดวงต่อหน้า) และเลือก Preset เครื่องพิมพ์/วัสดุ แล้วกด พิมพ์ (PDF) QR ที่ติดไว้ใช้สแกนตอนตรวจนับและเปิดหน้ารายละเอียดได้ทันที',
        },
        {
          kind: 'image',
          src: '/help/lva/08-qr.png',
          caption:
            'แท็บ QR Code Generator — QR ของแต่ละรายการพร้อมพิมพ์ และปุ่มเลือกขนาดสติกเกอร์ / Export PDF',
        },
      ],
    },
    {
      id: 'count',
      heading: '6. การตรวจนับของจริง (Physical Count)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Physical Count แสดงรายการรอบการตรวจนับ (รหัส PCR-YYYY-NNNNN) พร้อมขอบเขต (Scope) ความคืบหน้า (นับแล้ว/พบ/ไม่พบ/เพิ่ม) และสถานะ (กำลังนับ/รออนุมัติ/เสร็จ)',
        },
        {
          kind: 'image',
          src: '/help/lva/04-count.png',
          caption:
            'แท็บ Physical Count — รายการรอบตรวจนับพร้อมขอบเขตและความคืบหน้า (ปุ่ม นับต่อ → / ดูผล)',
        },
        {
          kind: 'steps',
          items: [
            {
              title: 'สร้างรอบใหม่',
              text: 'ระบุชื่อรอบ วันที่ และขอบเขต ระบบ snapshot รายการที่ต้องนับ',
            },
            {
              title: 'สแกน/นับ (Blind Count)',
              text: 'พนักงานสแกน QR ทีละชิ้น ระบบซ่อนตำแหน่งคาดหวังเพื่อความเที่ยงตรง',
            },
            {
              title: 'จำแนกผล',
              text: 'ของที่ไม่ได้สแกนจัดเป็น พบ/ไม่พบ/ย้ายที่/ชำรุด และบันทึกของที่พบนอกทะเบียน',
            },
            {
              title: 'ส่งปิดรอบ + อนุมัติ',
              text: 'กด ส่งปิดรอบ เข้าสู่การอนุมัติแบบ 4 ตา เมื่ออนุมัติขั้นสุดท้าย ระบบลงรายการเคลื่อนไหวให้อัตโนมัติ',
            },
          ],
        },
      ],
    },
    {
      id: 'movement',
      heading: '7. ประวัติการเคลื่อนไหว (Movement & Lifecycle)',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Movement & Lifecycle รวมประวัติการเคลื่อนไหวทั้งบริษัทไว้ที่เดียว (โอนย้าย/เปลี่ยนผู้ถือครอง/ชำรุด/สูญหาย/จำหน่าย) แสดงวันที่ เลขที่อ้างอิง ประเภท ทรัพย์สิน จาก→ไป ผู้บันทึก ผู้อนุมัติ และหมายเหตุ',
        },
        {
          kind: 'image',
          src: '/help/lva/05-movement.png',
          caption:
            'แท็บ Movement & Lifecycle — ประวัติการเคลื่อนไหวทั้งบริษัท พร้อมประเภทเหตุการณ์และผู้อนุมัติ',
        },
      ],
    },
    {
      id: 'reports',
      heading: '8. รายงาน & การกระทบยอด GL',
      blocks: [
        {
          kind: 'para',
          text: 'แท็บ Reports & Insights ส่วน “KPI & Aging” แสดงตัวชี้วัด (มูลค่าทะเบียน · อัตราการสูญหาย · % ติด QR Tag · ตรวจนับล่าสุด) พร้อมสรุปตามหมวด การกระจายตามสภาพ และอายุสินทรัพย์',
        },
        {
          kind: 'image',
          src: '/help/lva/07-reports.png',
          caption: 'Reports → KPI & Aging — ตัวชี้วัดหลัก สรุปตามหมวด และการกระจายตามสภาพ',
        },
        {
          kind: 'para',
          text: 'ด้านล่างเป็น “กระทบยอด GL (Memorandum vs GL)” เปรียบเทียบมูลค่าในทะเบียนกับยอดเดบิตค่าใช้จ่ายใน GL ทีละรายการ สถานะ: ตรงกัน (OK) · ผลต่าง (DRIFT) · ไม่พบ JV (MISSING_JV)',
        },
        {
          kind: 'image',
          src: '/help/lva/07b-tieout.png',
          caption:
            'กระทบยอด GL — เทียบมูลค่าทะเบียนกับเดบิต GL ต่อรายการ พร้อมสถานะ OK / DRIFT / MISSING_JV',
        },
      ],
    },
    {
      id: 'alerts',
      heading: '9. การแจ้งเตือนเชิงรุก (Alerts & Scheduling)',
      blocks: [
        {
          kind: 'para',
          text: 'ส่วน “Alerts & Scheduling” ช่วยวางแผนเชิงรุก แสดงการ์ดเตือน 5 กลุ่มและพยากรณ์การเปลี่ยนทดแทน พร้อมรายการ (Worklist) ให้ตามจัดการ:',
        },
        {
          kind: 'bullets',
          items: [
            { lead: 'ถึงกำหนดนับ — ', text: 'ของที่ถึงรอบตรวจนับตามวงรอบ' },
            { lead: 'ประกันใกล้หมด — ', text: 'ของที่ประกันใกล้หมดภายใน 90 วัน' },
            { lead: 'รอบเกินกำหนด — ', text: 'รอบตรวจนับที่เลยกำหนด' },
            { lead: 'ถึงเวลาเปลี่ยน — ', text: 'ของที่ถึงอายุควรเปลี่ยนทดแทน' },
            { lead: 'สภาพแย่ — ', text: 'ของที่สภาพทรุดโทรม/ชำรุด' },
          ],
        },
        {
          kind: 'image',
          src: '/help/lva/06-alerts.png',
          caption:
            'Reports → Alerts & Scheduling — การ์ดเตือน 5 กลุ่ม พยากรณ์การเปลี่ยนทดแทน และ Worklist',
        },
      ],
    },
    {
      id: 'import-export',
      heading: '10. การนำเข้าและส่งออกข้อมูล',
      blocks: [
        {
          kind: 'para',
          text: 'ส่งออก: ปุ่ม ส่งออก Excel ดาวน์โหลดรายการตามตัวกรองปัจจุบัน นำเข้า: ปุ่ม นำเข้า Excel สร้างของทีละหลายรายการจากไฟล์ (หนึ่งแถว = หนึ่งรายการ) ระบบสร้างใหม่และลงค่าใช้จ่ายให้อัตโนมัติ พร้อมเทมเพลตและพรีวิวก่อนสร้าง',
        },
        {
          kind: 'image',
          src: '/help/lva/09-import.png',
          caption:
            'หน้าต่างนำเข้าทะเบียน LVA — ตารางอ้างอิงคอลัมน์ที่ต้องกรอก พร้อมดาวน์โหลดเทมเพลต',
        },
      ],
    },
    {
      id: 'statuses',
      heading: '11. สรุปสถานะ',
      blocks: [
        {
          kind: 'table',
          head: ['สถานะทรัพย์สิน', 'ความหมาย'],
          rows: [
            ['ใช้งาน', 'ยังใช้งานอยู่ในระบบ (ดำเนินการ Lifecycle ได้)'],
            ['จำหน่าย', 'จำหน่าย/ขายซากแล้ว — ตัดออกจากการใช้งาน'],
            ['สูญหาย', 'แจ้งสูญหายแล้ว (ต้องบันทึก & รายงาน)'],
          ],
        },
        {
          kind: 'table',
          head: ['สถานะกระทบยอด GL', 'ความหมาย'],
          rows: [
            ['OK (ตรงกัน)', 'มูลค่าทะเบียน = ยอดเดบิตค่าใช้จ่ายใน GL'],
            ['DRIFT (ผลต่าง)', 'มีผลต่างระหว่างทะเบียนกับ GL — ต้องตรวจสอบ'],
            ['MISSING_JV (ไม่พบ JV)', 'ยังไม่พบใบสำคัญลงค่าใช้จ่ายของรายการนี้'],
          ],
        },
      ],
    },
    {
      id: 'faq',
      heading: '12. คำถามที่พบบ่อยและข้อควรระวัง',
      blocks: [
        { kind: 'para', text: 'ทำไมบันทึก LVA แล้วยอดงบดุลไม่เพิ่ม?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'ตั้งใจให้เป็นเช่นนั้น — LVA ลงเป็นค่าใช้จ่ายทันที ทะเบียนเป็นเพียง Memorandum (Off-balance)',
            },
          ],
        },
        { kind: 'para', text: 'กรอกมูลค่าแล้วบันทึกไม่ได้?' },
        {
          kind: 'bullets',
          items: [
            {
              text: 'มูลค่าต้อง < ฿50,000 ถ้าตั้งแต่เกณฑ์ขึ้นไป ต้องลงทะเบียนเป็นสินทรัพย์ถาวร (หน้า /fixedasset) แทน',
            },
            { text: 'ต้องกรอก ชื่อ · มูลค่า · สถานที่ใช้งาน · ผู้รับผิดชอบ ให้ครบ' },
          ],
        },
      ],
    },
    {
      id: 'glossary',
      heading: '13. อภิธานศัพท์',
      blocks: [
        {
          kind: 'bullets',
          items: [
            { lead: 'LVA — ', text: 'ของใช้/อุปกรณ์มูลค่าต่ำกว่าเกณฑ์ตั้งเป็นสินทรัพย์ถาวร' },
            {
              lead: 'Memorandum / Off-balance — ',
              text: 'บันทึกความจำเพื่อติดตามของจริง ไม่กระทบยอดงบดุล',
            },
            { lead: 'Physical Count — ', text: 'การนับของจริงเทียบทะเบียนเป็นรอบ ๆ' },
            { lead: 'Blind Count — ', text: 'การนับโดยซ่อนตำแหน่งคาดหวังเพื่อความเที่ยงตรง' },
            { lead: 'Tie-out — ', text: 'กระทบยอดทะเบียนกับยอดค่าใช้จ่ายใน GL' },
            {
              lead: 'แยกหน้าที่ (SoD) — ',
              text: 'ผู้บันทึกกับผู้อนุมัติต้องเป็นคนละคน (แจ้งสูญหาย/จำหน่าย/ปิดรอบนับ)',
            },
          ],
        },
      ],
    },
  ],
};
